import { createQuickQuoteReplyDraft } from '@src/alexandria/lib/api/createQuickQuoteReplyDraft';
import { createQuickQuoteReplySend } from '@src/alexandria/lib/api/createQuickQuoteReplySend';
import type {
  DraftReplyProps,
  MailClientInterface,
} from '@src/alexandria/lib/mailclient/interface';
import type { Undef } from '@src/alexandria/types/UtilityTypes';

const DEFAULT_DRAFT_REPLY_MODE =
  'replyAll' satisfies DraftReplyProps['replyMode'];

const handleReplyDraftCreation = async ({
  threadItemId,
  draftBody,
  replyMode = DEFAULT_DRAFT_REPLY_MODE,
  to,
  cc,
  bcc,
}: DraftReplyProps) => {
  const result = await createQuickQuoteReplyDraft({
    threadItemId,
    draftBody,
    replyMode,
    to,
    cc,
    bcc,
  });

  if (result.isErr()) {
    throw result.error;
  }
};

const handleReplySend = async ({
  threadItemId,
  draftBody,
  replyMode = DEFAULT_DRAFT_REPLY_MODE,
  to,
  cc,
  bcc,
}: DraftReplyProps) => {
  const result = await createQuickQuoteReplySend({
    threadItemId,
    draftBody,
    replyMode,
    to,
    cc,
    bcc,
  });

  if (result.isErr()) {
    throw result.error;
  }
};

const setComposeRecipients = async (
  recipients: Undef<Office.Recipients>,
  emails: Undef<string[]>
): Promise<void> => {
  if (!recipients || emails === undefined) {
    return;
  }

  await new Promise<void>((resolve, reject) => {
    recipients.setAsync(emails, (asyncResult) => {
      if (asyncResult.status === Office.AsyncResultStatus.Failed) {
        reject(asyncResult.error);
        return;
      }
      resolve();
    });
  });
};

const prependComposeBody = async (
  item: Office.MessageCompose,
  draftBody: string
): Promise<void> => {
  await new Promise<void>((resolve, reject) => {
    item.body.prependAsync(
      draftBody,
      { coercionType: Office.CoercionType.Html },
      (setResult) => {
        if (setResult.status === Office.AsyncResultStatus.Failed) {
          reject(setResult.error);
          return;
        }
        resolve();
      }
    );
  });
};

export class Client implements MailClientInterface {
  draftReply({
    threadItemId,
    draftBody,
    replyMode,
    to,
    cc,
    bcc,
  }: DraftReplyProps): Promise<void> {
    return new Promise((resolve, reject) => {
      Office.onReady((info: any) => {
        if (info.host !== Office.HostType.Outlook) {
          reject(
            new Error(
              `Office host is ${info.host}, not ${Office.HostType.Outlook} as expected`
            )
          );
          return;
        }
      });

      const item = Office.context.mailbox.item;
      if (!item) {
        reject(new Error(`Compose item is falsy (${item})`));
        return;
      }

      // If we're not in reply view, create reply draft on the backend
      if (!item.body.getTypeAsync) {
        handleReplyDraftCreation({
          threadItemId,
          draftBody,
          replyMode,
          to,
          cc,
          bcc,
        })
          .then(() => resolve())
          .catch(reject);
        return;
      }

      item.body.getTypeAsync(async (asyncResult: any) => {
        if (asyncResult.status === Office.AsyncResultStatus.Failed) {
          reject(asyncResult.error);
          return;
        }

        try {
          // Set recipients before body prepend so a recipient failure does not
          // leave the quote already written (retry would double-prepend).
          // Sequential Office.js writes avoid concurrent host-side races.
          await setComposeRecipients(item.to, to);
          await setComposeRecipients(item.cc, cc);
          await setComposeRecipients(item.bcc, bcc);
          await prependComposeBody(item, draftBody);
          resolve();
        } catch (error) {
          reject(error);
        }
      });
    });
  }

  sendReply({
    threadItemId,
    draftBody,
    replyMode,
    to,
    cc,
    bcc,
  }: DraftReplyProps): Promise<void> {
    return new Promise((resolve, reject) => {
      Office.onReady((info: any) => {
        if (info.host !== Office.HostType.Outlook) {
          reject(
            new Error(
              `Office host is ${info.host}, not ${Office.HostType.Outlook} as expected`
            )
          );
          return;
        }
      });

      const item = Office.context.mailbox.item;
      if (!item) {
        reject(new Error(`Compose item is falsy (${item})`));
        return;
      }

      if (!item.body.getTypeAsync) {
        handleReplySend({
          threadItemId,
          draftBody,
          replyMode,
          to,
          cc,
          bcc,
        })
          .then(() => resolve())
          .catch(reject);
        return;
      }

      reject(
        new Error('Send reply is only supported from the email read view')
      );
    });
  }
}
